import { get, post, put } from './api';
import type {
  ScrapeProfile,
  ScrapeProfileRequest,
  ScrapeRunDetail,
  ScrapeRunSummary,
} from '../types/scraping';

const BASE_URL = '/api/admin/scrape-profiles';

/**
 * A run renders a page in headless Chromium and then calls an LLM, so it can take tens of seconds.
 * The request is synchronous by design — a developer iterating a prompt wants the answer, not a
 * polling loop — so it needs a timeout well above the API's own render ceiling (300s) plus
 * extraction. If the client does give up, the run row is still persisted server-side and shows up in
 * the history.
 */
export const SCRAPE_RUN_TIMEOUT_MS = 420_000;

export async function getScrapeProfiles(
  includeArchived = false,
  signal?: AbortSignal
): Promise<ScrapeProfile[]> {
  const params = new URLSearchParams({ includeArchived: String(includeArchived) });
  return get<ScrapeProfile[]>(`${BASE_URL}?${params}`, { signal });
}

export async function getScrapeProfile(
  profileId: string,
  signal?: AbortSignal
): Promise<ScrapeProfile> {
  return get<ScrapeProfile>(`${BASE_URL}/${profileId}`, { signal });
}

export async function createScrapeProfile(
  body: ScrapeProfileRequest,
  signal?: AbortSignal
): Promise<ScrapeProfile> {
  return post<ScrapeProfile>(BASE_URL, body, { signal });
}

export async function updateScrapeProfile(
  profileId: string,
  body: ScrapeProfileRequest,
  signal?: AbortSignal
): Promise<ScrapeProfile> {
  return put<ScrapeProfile>(`${BASE_URL}/${profileId}`, body, { signal });
}

/**
 * Archive hides a profile from the default list but keeps its run history readable. Profiles are
 * never hard-deleted, so historical runs always resolve to a parent.
 */
export async function archiveScrapeProfile(
  profileId: string,
  signal?: AbortSignal
): Promise<ScrapeProfile> {
  return post<ScrapeProfile>(`${BASE_URL}/${profileId}/archive`, undefined, { signal });
}

export async function restoreScrapeProfile(
  profileId: string,
  signal?: AbortSignal
): Promise<ScrapeProfile> {
  return post<ScrapeProfile>(`${BASE_URL}/${profileId}/restore`, undefined, { signal });
}

/**
 * Run a profile now. Resolves with the finished run even when that run FAILED — "this site does not
 * render" and "this prompt does not work" are results, and they carry the raw content needed to tell
 * them apart. Only a transport-level problem rejects.
 */
export async function runScrapeProfile(
  profileId: string,
  signal?: AbortSignal
): Promise<ScrapeRunDetail> {
  return post<ScrapeRunDetail>(`${BASE_URL}/${profileId}/runs`, undefined, {
    timeout: SCRAPE_RUN_TIMEOUT_MS,
    signal,
  });
}

export async function getScrapeRuns(
  profileId: string,
  take = 25,
  signal?: AbortSignal
): Promise<ScrapeRunSummary[]> {
  const params = new URLSearchParams({ take: String(take) });
  return get<ScrapeRunSummary[]>(`${BASE_URL}/${profileId}/runs?${params}`, { signal });
}

export async function getScrapeRun(
  runId: string,
  signal?: AbortSignal
): Promise<ScrapeRunDetail> {
  return get<ScrapeRunDetail>(`${BASE_URL}/runs/${runId}`, { signal });
}
